import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Zap, Activity, GitBranch, Layers, Fingerprint } from 'lucide-react'
import { motion, useInView, useMotionValue, useSpring, animate } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { iceSmoothScroll } from '../utils/smoothScroll'
import { sendMetaEvent } from '../utils/metaCapi'
import { AwardBadge } from './ui/award-badge'

function CountUp({ target, suffix = '', prefix = '', decimals = 0, duration = 1.8, delay = 0, className = '' }) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })
  const count = useMotionValue(0)
  const spring = useSpring(count, { stiffness: 50, damping: 20, duration: duration * 1000 })
  const [display, setDisplay] = React.useState('0')

  useEffect(() => {
    if (!isInView) return
    const timer = setTimeout(() => {
      const controls = animate(count, target, {
        duration,
        ease: [0.25, 0.1, 0.25, 1],
      })
      return controls.stop
    }, delay * 1000)
    return () => clearTimeout(timer)
  }, [isInView, target, duration, delay, count])

  useEffect(() => {
    const unsubscribe = spring.on('change', (latest) => {
      const formatted = latest.toFixed(decimals)
      setDisplay(`${prefix}${formatted}${suffix}`)
    })
    return unsubscribe
  }, [spring, decimals, prefix, suffix])

  return <span ref={ref} className={className}>{display}</span>
}

const Hero = () => {
  const { t } = useTranslation()
  const handleScrollTo = (e, target) => {
    e.preventDefault()
    iceSmoothScroll(target, 1200)
  }

  const [activeWorkflow, setActiveWorkflow] = useState(0)
  const cardRef = useRef(null)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveWorkflow((prev) => (prev + 1) % 3)
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect()
        const x = (e.clientX - rect.left - rect.width / 2) / 20
        const y = (e.clientY - rect.top - rect.height / 2) / 20
        setMousePos({ x, y })
      }
    }

    const card = cardRef.current
    if (card) {
      card.addEventListener('mousemove', handleMouseMove)
      return () => card.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  const stats = [
    { icon: Zap, value: 20, suffix: '+', label: t('hero.hoursSaved', 'Hours Saved/Week'), color: 'text-purple-400' },
    { icon: Layers, value: 50, suffix: '+', label: t('hero.workflowsDelivered', 'Workflows Delivered'), color: 'text-purple-400' },
    { icon: Activity, value: 94.7, suffix: '%', decimals: 1, label: t('hero.clientSatisfaction', 'Client Satisfaction'), color: 'text-green-400' },
  ]

  const workflows = [
    { name: t('hero.leadGen', 'Lead Generation'), triggers: 273, triggersFormatted: '273', success: 94.2, icon: '🎯' },
    { name: t('hero.crmSync', 'CRM Sync'), triggers: 1849, triggersFormatted: '1,849', success: 98.7, icon: '🔄' },
    { name: t('hero.emailAuto', 'Email Automation'), triggers: 5367, triggersFormatted: '5,367', success: 96.3, icon: '📧' },
  ]

  const dashboardStats = [
    { value: 8500, suffix: '+', label: t('hero.executionsToday', 'Executions Today'), color: 'text-white' },
    { value: 98.6, suffix: '%', decimals: 1, label: t('hero.successRate', 'Success Rate'), color: 'text-purple-400' },
    { value: null, static: '24/7', label: t('hero.activeWorkflows', 'Active Workflows'), color: 'text-purple-400' },
  ]

  return (
    <section className="relative min-h-[100dvh] flex items-center pt-24 pb-16 px-4 sm:px-6 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[#0a0a0f]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-purple-600/25 rounded-full blur-[150px] animate-pulse-slow"></div>
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[300px] bg-purple-500/10 rounded-full blur-[80px]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
      </div>

      <div className="container-custom relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content - Text */}
          <div className="space-y-8 max-w-2xl order-2 lg:order-1">
            {/* Product Hunt Award Badge */}
            <div dir="ltr">
              <AwardBadge type="product-of-the-day" place={1} customLabel="Automation Experts" customLabelSecond="NODATX" link="https://www.instagram.com/nodatx.c0m" />
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.15]">
              <span className="text-white">{t('hero.headline1', 'Automate Your Business')}</span>
              <span className="block lg:inline"> </span>
              <span className="text-purple-400">{t('hero.headline2', 'In Days, Not Months')}</span>
            </h1>

            {/* Subheadline */}
            <p className="text-base sm:text-lg text-gray-400 max-w-xl leading-relaxed">
              {t('hero.sub1', 'We build intelligent n8n workflows that eliminate repetitive tasks, connect your tools, and save you')}{' '}
              <span className="text-white font-semibold border-b-2 border-purple-500/50 pb-0.5">{t('hero.subHighlight', '20+ hours')}</span> {t('hero.sub2', 'every week — without enterprise-level costs.')}
            </p>

            {/* CTA Buttons - Mobile Optimized */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Primary CTA */}
              <a
                href="https://cal.com/mostafa-elsheikh/30min"
                target="_blank"
                rel="noreferrer"
                className="group flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 sm:px-8 bg-purple-600 text-white font-semibold rounded-xl overflow-hidden transition-all duration-200 hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.5)] active:scale-[0.98] text-sm sm:text-base"
                onClick={() => sendMetaEvent('Lead', 'book_call')}
              >
                <span className="flex items-center gap-2">
                  {t('hero.bookCall', 'Book a Call')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </a>
              
              {/* Secondary CTAs - Stack on very small screens */}
              <div className="flex gap-2">
                <a
                  href="https://wa.me/201010668822"
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-3.5 bg-[#25D366] text-white font-medium rounded-xl overflow-hidden transition-all duration-200 hover:shadow-[0_0_30px_-10px_rgba(37,211,102,0.4)] active:scale-[0.98] text-sm"
                  onClick={() => sendMetaEvent('Contact', 'whatsapp_click')}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
                  </svg>
                  <span className="hidden xs:inline">{t('hero.whatsapp', 'WhatsApp')}</span>
                </a>
                
                <a 
                  href="#services" 
                  onClick={(e) => handleScrollTo(e, '#services')}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-3.5 bg-white/5 border border-white/20 text-white font-medium rounded-xl hover:bg-white/10 hover:border-purple-500/50 active:scale-[0.98] transition-all duration-200 text-sm backdrop-blur-md"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="hidden xs:inline">{t('hero.howItWorks', 'How It Works')}</span>
                </a>
              </div>
            </div>

            {/* Stats - Better spacing from CTAs */}
            <div className="pt-6 border-t border-white/10">
              <div className="flex flex-wrap items-center justify-start gap-4 sm:gap-8">
                {stats.map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.15, duration: 0.4 }}
                    className="flex items-center gap-2 sm:gap-3"
                  >
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/5 flex items-center justify-center ${stat.color}`}>
                      <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div>
                      <CountUp
                        target={stat.value}
                        suffix={stat.suffix}
                        decimals={stat.decimals || 0}
                        delay={i * 0.15}
                        className="text-lg sm:text-2xl font-bold text-white"
                      />
                      <div className="text-gray-500 text-[10px] sm:text-xs whitespace-nowrap">{stat.label}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* PayrollX Highlight Banner */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="relative group"
            >
              <Link
                to="/packages"
                onClick={() => sendMetaEvent('SectionClick', { content_name: 'PayrollX Packages', section_id: 'packages', page_path: '/' })}
                className="relative block overflow-hidden rounded-2xl border border-purple-500/40 bg-gradient-to-r from-purple-600/15 via-purple-500/10 to-purple-600/15 p-4 sm:p-5 transition-all duration-300 hover:border-purple-400/70 hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.45)]"
              >
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-purple-500/30 rounded-full blur-[40px] group-hover:bg-purple-500/40 transition-colors" />
                <div className="relative z-10 flex items-center gap-3 sm:gap-4">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_-5px_rgba(139,92,246,0.6)]">
                    <Fingerprint className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-purple-400">{t('hero.newProduct', 'New Product')}</span>
                      <span className="px-1.5 py-0.5 rounded-full bg-purple-500/20 text-[9px] sm:text-[10px] font-semibold text-purple-300">{t('hero.packages', 'Packages')}</span>
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-white leading-snug">
                      <span className="text-purple-400">PayrollX</span> — Attendance, payroll & violations
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-400 mt-0.5 leading-snug line-clamp-2 sm:truncate">
                      Configure your package — pick components, see live price
                    </p>
                  </div>
                  <div className="hidden sm:flex flex-col items-end gap-0.5 flex-shrink-0">
                    <span className="text-xs text-gray-400">Starting from</span>
                    <span className="text-base font-bold text-white">EGP 15,000</span>
                  </div>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </motion.div>
          </div>

          {/* Right Content - Dashboard with Smooth 3D Effect */}
          <div className="relative hidden lg:block order-1 lg:order-2 perspective-1000">
            <div 
              ref={cardRef}
              className="relative transition-transform duration-500 ease-out"
              style={{
                transform: `perspective(1000px) rotateY(${mousePos.x}deg) rotateX(${-mousePos.y}deg)`,
              }}
            >
              {/* Main Dashboard Card */}
              <div className="relative bg-[#0f0f1a]/95 backdrop-blur-2xl border border-purple-500/20 rounded-3xl p-6 shadow-[0_0_100px_-30px_rgba(139,92,246,0.25)] overflow-hidden">
                {/* Glow */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-[60px]"></div>
                
                {/* Dashboard Header */}
                <div className="flex items-center justify-between mb-6 relative z-10">
                  <div className="flex items-center gap-3">
                    <motion.div
                      initial={{ rotate: -10, scale: 0.8, opacity: 0 }}
                      animate={{ rotate: 0, scale: 1, opacity: 1 }}
                      transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
                      className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-600 rounded-xl flex items-center justify-center"
                    >
                      <GitBranch className="w-5 h-5 text-white" />
                    </motion.div>
                    <div>
                      <div className="text-white font-bold">{t('hero.dashboardTitle', 'Nodatx Dashboard')}</div>
                      <div className="text-gray-500 text-xs">{t('hero.dashboardSub', 'Real-time Automation')}</div>
                    </div>
                  </div>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4, duration: 0.4 }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 rounded-full"
                  >
                    <Activity className="w-3 h-3 text-green-400 animate-pulse" />
                    <span className="text-green-400 text-xs font-medium">{t('hero.live', 'Live')}</span>
                  </motion.div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {dashboardStats.map((stat, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 16, scale: 0.94 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 0.2 + i * 0.12, duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
                      className="bg-white/5 rounded-xl p-4 border border-white/5"
                    >
                      {stat.static ? (
                        <div className={`text-2xl font-bold ${stat.color}`}>{stat.static}</div>
                      ) : (
                        <CountUp
                          target={stat.value}
                          suffix={stat.suffix}
                          decimals={stat.decimals || 0}
                          delay={0.2 + i * 0.12}
                          duration={1.6}
                          className={`text-2xl font-bold ${stat.color}`}
                        />
                      )}
                      <div className="text-gray-500 text-xs">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>

                {/* Active Workflows */}
                <div className="space-y-3 relative z-10">
                  <div className="text-gray-400 text-xs font-medium mb-2">{t('hero.workflowsLabel', 'Active Workflows')}</div>
                  {workflows.map((workflow, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + i * 0.12, duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                      className={`flex items-center justify-between p-3 rounded-xl border transition-all duration-300 ${
                        activeWorkflow === i 
                          ? 'bg-purple-500/10 border-purple-500/40' 
                          : 'bg-white/5 border-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-xl">{workflow.icon}</div>
                        <div>
                          <div className="text-white font-medium text-sm">{workflow.name}</div>
                          <div className="text-gray-500 text-xs">
                            <CountUp target={workflow.triggers} delay={1 + i * 0.15} duration={1.2} className="tabular-nums" /> {t('hero.triggersToday', 'triggers today')}
                          </div>
                        </div>
                      </div>
                      <div className={`text-sm font-semibold ${i === 1 ? 'text-green-400' : 'text-purple-400'}`}>
                        <CountUp target={workflow.success} suffix="%" decimals={1} delay={1.2 + i * 0.15} duration={0.8} />
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Node Visual */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2, duration: 0.5 }}
                  className="mt-6 pt-4 border-t border-white/10"
                >
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    <span>{t('hero.connections', 'Workflow Connections')}</span>
                    <span className="text-purple-400">{t('hero.nodesActive', '12 nodes active')}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    {['📥', '🔍', '📊', '✉️', '💾', '🤖', '✅'].map((icon, i) => (
                      <motion.div
                        key={i} 
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1.3 + i * 0.08, duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-all duration-500 ${
                          i % 2 === 0 ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-500/20 text-purple-400'
                        }`}
                      >
                        {icon}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero